import { Component, Input, OnChanges } from '@angular/core';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, NOT_ASKED_LABEL, ReportBlock, tint } from '../report-aggregation';
import type { BlockPatch } from '../report-design-edit';
import { readRanks } from './rank-shares';
import { textOn } from './summary-boxes.component';

/** How pale the weakest cell in a column gets, and how strong the busiest. */
const PALEST = 0.94;
const STRONGEST = 0.12;

/** One cell of the matrix. */
interface Cell {
  text: string;
  fill: string;
  ink: string;
}

/** One attribute. */
interface Row {
  attribute: string;
  /** The "how much does this matter" figure, when a ranking question was given. */
  priority: Cell | null;
  priorityValue: number;
  cells: Cell[];
  /** What nobody owns: 100 minus the brands on this row. */
  whiteSpace: Cell | null;
}

/**
 * Ownership heatmap: which brand owns which attribute.
 *
 * Every cell is one number -- the share associating a brand with an attribute -- and the module is
 * entirely about making a grid of those readable: one colour scale across the whole matrix so the
 * columns are comparable with each other, and two derived columns either side that answer the
 * questions a reader actually arrives with. Which of these matters? Who owns it? What does nobody
 * own?
 *
 * **White space is the point of it.** A high figure on an attribute people rank highly is an
 * opening, and no chart of brand shares alone will show one.
 *
 * The priority column is computed by `rank-shares.ts`, the same code the ranked driver draws its
 * bars from, because the two modules regularly sit on facing pages of the same deck.
 */
@Component({
  selector: 'app-ownership-heatmap-block',
  standalone: false,
  template: `
    <div class="heat" [class.compact]="compact" [style.--cols]="columns.length">
      @if (rows.length) {
        <table class="grid">
          <thead>
            <tr>
              <th class="attr" scope="col">Attribute</th>
              @if (showPriority) {
                <!-- The two derived columns' names are written in place while editing. -->
                <th class="derived" scope="col">
                  @if (editing) {
                    <span class="d-flex align-items-center">★
                      <input type="text" class="ecr-inline-input ml-1" maxlength="40"
                             aria-label="Name of the priority column" [placeholder]="priorityDefault"
                             [value]="priorityText"
                             (input)="set('priorityLabel', $any($event.target).value)">
                    </span>
                  } @else {
                    ★ {{ priorityLabel }}
                  }
                </th>
              }
              @for (column of columns; track column) {
                <th scope="col">{{ column }}</th>
              }
              @if (showWhiteSpace) {
                <th class="derived" scope="col">
                  @if (editing) {
                    <input type="text" class="ecr-inline-input" maxlength="40"
                           aria-label="Name of the unowned column" placeholder="White Space"
                           [value]="whiteSpaceText"
                           (input)="set('whiteSpaceLabel', $any($event.target).value)">
                  } @else {
                    {{ whiteSpaceLabel }}
                  }
                </th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of rows; track row.attribute) {
              <tr>
                <th class="attr" scope="row">{{ row.attribute }}</th>
                @if (showPriority) {
                  <td [style.background]="row.priority?.fill" [style.color]="row.priority?.ink">
                    {{ row.priority?.text }}
                  </td>
                }
                @for (cell of row.cells; track $index) {
                  <td [style.background]="cell.fill" [style.color]="cell.ink">{{ cell.text }}</td>
                }
                @if (showWhiteSpace) {
                  <td [style.background]="row.whiteSpace?.fill" [style.color]="row.whiteSpace?.ink">
                    {{ row.whiteSpace?.text }}
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      }

      @for (problem of problems; track problem) {
        <p class="note grey4 mb-0 mt-2">{{ problem }}</p>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }

    .heat { font-size: 13px; }
    .heat.compact { font-size: 11px; }

    /* Scrolls rather than squeezing: a matrix with eight brands cannot be made narrow, and a cell
       too small to hold "31%" is a cell that says nothing. */
    .grid {
      width: 100%;
      border-collapse: separate;
      border-spacing: 2px;
      table-layout: fixed;
    }

    th, td {
      padding: 6px 4px;
      text-align: center;
      font-weight: 400;
      line-height: 1.25;
      border-radius: 3px;
    }
    .heat.compact th, .heat.compact td { padding: 4px 2px; }

    thead th {
      font-size: 0.92em;
      font-weight: 600;
      color: var(--ecr-ink, #1b2b33);
      vertical-align: bottom;
      border-bottom: 1px solid var(--nav-grey1, #f2f2f2);
      padding-bottom: 8px;
    }

    /* The attribute column is the one that has to be readable at length, so it takes the width and
       the only left-aligned text on the card. */
    .attr {
      width: 30%;
      text-align: left;
      font-weight: 400;
      color: var(--ecr-ink, #1b2b33);
    }
    thead .attr { font-weight: 600; }

    tbody td { font-size: 0.92em; }

    .note { font-size: 0.85em; line-height: 1.35; }
  `],
})
export class OwnershipHeatmapBlockComponent implements ReportBlock, OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() compact?: boolean;
  /** Set while the deck is in edit mode, when the derived columns' names are written in place. */
  @Input() editing?: boolean;
  @Input() onPatch?: (patch: BlockPatch) => void;

  columns: string[] = [];
  rows: Row[] = [];
  problems: string[] = [];
  showPriority = false;
  showWhiteSpace = true;
  priorityLabel = '';
  whiteSpaceLabel = '';
  /** The priority column's name when none is written, from how many ranks it counts. */
  priorityDefault = '';
  /** The two names exactly as stored, so an input being cleared is not refilled under the cursor. */
  priorityText = '';
  whiteSpaceText = '';

  ngOnChanges() {
    this.build();
  }

  set(field: 'priorityLabel' | 'whiteSpaceLabel', value: string) {
    this.onPatch?.({ [field]: value });
  }

  private build() {
    this.columns = [];
    this.rows = [];
    this.problems = [];
    this.showPriority = false;
    if (this.block.reportType !== 'ownership-matrix') return;

    const extra = this.block.reportExtraData;
    // The design's own two lists, in order: the attributes first, the ranking grid after. The block
    // knows how many of each it asked for, so slicing is exact rather than a guess.
    const matrix = this.series.slice(0, extra.variableKeys.length);
    const ranking = this.series.slice(extra.variableKeys.length);

    const usable = matrix.filter(s => s.known);
    for (const s of matrix) {
      if (!s.known) this.problems.push(`No data for ${s.name}.`);
    }
    if (!usable.length) return;

    this.columns = brandColumns(usable);
    if (!this.columns.length) {
      this.problems.push('These questions have no answers in common, so there are no columns to '
        + 'draw. An ownership matrix needs one question per attribute, each listing the same brands.');
      return;
    }

    this.showWhiteSpace = extra.showWhiteSpace !== false;
    this.whiteSpaceLabel = extra.whiteSpaceLabel?.trim() || 'White Space';
    this.whiteSpaceText = extra.whiteSpaceLabel ?? '';
    this.priorityText = extra.priorityLabel ?? '';

    const priority = this.priorityShares(ranking, extra);
    this.showPriority = priority !== null;

    // One scale across every brand cell, so a column full of small numbers looks like a column full
    // of small numbers. Shading each column against its own range would make the weakest brand's
    // best attribute as dark as the strongest brand's.
    const peak = Math.max(1, ...usable.flatMap(s =>
      real(s).filter(point => this.columns.includes(point.label)).map(point => point.percent)));

    this.rows = usable.map(s => this.rowFor(s, peak, priority));

    if (priority) {
      const ranked = new Set(priority.keys());
      const unmatched = this.rows.filter(row => !ranked.has(row.attribute)).map(row => row.attribute);
      if (unmatched.length) {
        this.problems.push(`No priority figure for ${unmatched.join(', ')}: `
          + 'the ranking question did not ask about them under the same wording.');
      }
    }

    const order = extra.rowOrder ?? (this.showPriority ? 'priority' : 'asked');
    if (order === 'priority' && this.showPriority) {
      // Stable on a tie, so two attributes level on priority keep the order they were asked in.
      this.rows = [...this.rows].sort((a, b) => b.priorityValue - a.priorityValue);
    }

    this.shadeDerived(peak);
  }

  /** The top-N share per attribute, or null where no ranking question was given. */
  private priorityShares(
    ranking: ChartSeries[],
    extra: { priorityKeys?: string[]; topRanks?: number; rankOneEnd?: 'first' | 'last';
      priorityLabel?: string },
  ): Map<string, number> | null {
    if (!extra.priorityKeys?.length || !ranking.length) return null;

    const reading = readRanks(ranking, extra);
    this.problems.push(...reading.problems);
    if (!reading.rankCount) return null;

    this.priorityDefault = `Top ${reading.topRanks} Priority`;
    this.priorityLabel = extra.priorityLabel?.trim() || this.priorityDefault;
    return new Map(reading.rows.map(row => [row.attribute, row.top]));
  }

  private rowFor(s: ChartSeries, peak: number, priority: Map<string, number> | null): Row {
    const byLabel = new Map(real(s).map(point => [point.label, point.percent]));
    const cells = this.columns.map(column => {
      const value = byLabel.get(column) ?? 0;
      return this.cell(value, this.shade(this.palette.secondary, value, peak));
    });

    const owned = this.columns.reduce((sum, column) => sum + (byLabel.get(column) ?? 0), 0);
    const share = priority?.get(s.name);

    return {
      attribute: s.name,
      priority: null,
      priorityValue: share ?? -1,
      cells,
      // 100 minus what the brands account for. Clamped, because a multi-punch question lets a
      // respondent pick several brands and a row can legitimately run past 100 -- which is not
      // negative white space, it is no white space.
      whiteSpace: this.showWhiteSpace
        ? this.cell(Math.max(0, 100 - owned), '')
        : null,
    };
  }

  /**
   * The two derived columns, shaded once the rows exist.
   *
   * Separately from the matrix because each has its own scale and its own meaning: priority runs
   * from strong to weak on the deck's own positive and accent colours, white space is neutral. On
   * the matrix's blue they would read as two more brands.
   */
  private shadeDerived(peak: number) {
    const priorities = this.rows.map(row => row.priorityValue).filter(value => value >= 0);
    const top = Math.max(1, ...priorities);

    for (const row of this.rows) {
      if (this.showPriority) {
        const value = Math.max(0, row.priorityValue);
        row.priority = this.cell(value, blend(this.palette.accent, this.palette.positive, value / top));
      }
      if (row.whiteSpace) {
        const value = Number.parseFloat(row.whiteSpace.text);
        row.whiteSpace = this.cell(value, this.shade('#7b8794', value, Math.max(peak, top, 100)));
      }
    }
  }

  private cell(value: number, fill: string): Cell {
    return {
      text: `${Math.round(value)}%`,
      fill,
      // Read off the fill rather than guessed: the palette is the client's, and a dark brand colour
      // with dark text on it is unreadable on exactly the cells that matter most.
      ink: fill ? textOn(fill) : 'var(--ecr-ink, #1b2b33)',
    };
  }

  /** Paler the smaller the figure, on one scale shared by every cell it is used for. */
  private shade(hex: string, value: number, peak: number): string {
    const strength = peak > 0 ? Math.min(1, Math.max(0, value / peak)) : 0;
    return tint(hex, PALEST - strength * (PALEST - STRONGEST));
  }
}

/** Real answers only: "Not asked" is a gap in a compare column, not a brand. */
function real(series: ChartSeries) {
  return series.points.filter(point => point.optionIndex >= 0 && point.label !== NOT_ASKED_LABEL);
}

/**
 * The brands, in the order the survey asked them.
 *
 * A union rather than the first row's answers: `aggregate()` drops an option nobody picked, so a
 * brand no one associated with the first attribute would otherwise disappear from the whole matrix.
 * Ordered by the position each brand holds in its own question, so the columns read as the survey
 * wrote them rather than as the first row happened to use them.
 */
function brandColumns(series: readonly ChartSeries[]): string[] {
  const at = new Map<string, number>();
  for (const s of series) {
    for (const point of real(s)) {
      const seen = at.get(point.label);
      if (seen === undefined || point.optionIndex < seen) at.set(point.label, point.optionIndex);
    }
  }
  return [...at.entries()].sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0])).map(([label]) => label);
}

/**
 * A colour between two others.
 *
 * Its own arithmetic rather than `tint`, which mixes toward white and so cannot express a ramp from
 * one palette colour to another. Used only by the priority column, where weak-to-strong is a
 * judgement the deck's own accent and positive colours already carry.
 */
function blend(from: string, to: string, amount: number): string {
  const channels = (hex: string) => {
    const clean = hex.replace('#', '');
    const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
    return [0, 2, 4].map(at => Number.parseInt(full.slice(at, at + 2), 16) || 0);
  };
  const a = channels(from);
  const b = channels(to);
  const t = Math.min(1, Math.max(0, amount));
  const mixed = a.map((value, i) => Math.round(value + (b[i] - value) * t));
  return `#${mixed.map(value => value.toString(16).padStart(2, '0')).join('')}`;
}
